import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellOff, BellRing, Download, Play, Volume2, VolumeX } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { cn } from '../../lib/utils';
import { useLanguage } from '../../context/LanguageContext';
import {
  CHAT_NOTIFY_BANNER_KEY,
  bootstrapChatNotify,
  loadNotifyPrefs,
  notificationPermission,
  playChatSound,
  requestNotifyPermission,
  saveNotifyPrefs,
  showChatDesktopNotification,
  subscribeNotifyPrefs,
  unlockChatAudio,
} from '../../lib/chatNotify';
import { isStandalonePwa, openPwaInstallPrompt } from '../../lib/pwaInstall';
import useHumanQueue from './useHumanQueue';

function PrefRow({ label, hint, checked, onCheckedChange, onPreview, disabled }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <div className="min-w-0">
        <p className="text-[13px] font-medium leading-tight text-foreground">{label}</p>
        {hint ? <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{hint}</p> : null}
      </div>
      <div className="flex shrink-0 items-center gap-1.5 pt-0.5">
        {onPreview ? (
          <button
            type="button"
            onClick={onPreview}
            className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
            title={label}
          >
            <Play className="h-3.5 w-3.5" />
          </button>
        ) : null}
        <Switch aria-label={label} checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
      </div>
    </div>
  );
}

export function ChatNotifyPanel({ showInboxLink = false, onClose }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [prefs, setPrefs] = useState(loadNotifyPrefs);
  const [perm, setPerm] = useState(notificationPermission);
  const [asking, setAsking] = useState(false);
  const [testStatus, setTestStatus] = useState(''); // '', 'ok', 'fail'

  useEffect(() => {
    bootstrapChatNotify();
    return subscribeNotifyPrefs(setPrefs);
  }, []);

  useEffect(() => {
    const sync = () => setPerm(notificationPermission());
    sync();
    document.addEventListener('visibilitychange', sync);
    window.addEventListener('focus', sync);
    return () => {
      document.removeEventListener('visibilitychange', sync);
      window.removeEventListener('focus', sync);
    };
  }, []);

  const update = (partial) => {
    unlockChatAudio();
    setPrefs(saveNotifyPrefs(partial));
  };

  const askPermission = async () => {
    setAsking(true);
    try {
      const result = await requestNotifyPermission({
        title: t('chats.notifyTitle'),
        body: t('chats.notifyTestPush'),
      });
      setPerm(result === 'granted' || result === 'denied' || result === 'default' ? result : notificationPermission());
      setPrefs(loadNotifyPrefs());
    } finally {
      setAsking(false);
    }
  };

  const onPushToggle = async (on) => {
    if (!on) {
      update({ desktopPush: false });
      return;
    }
    // Sempre passar pelo pedido/teste no gesto do utilizador (mostra diálogo se default;
    // se já granted, dispara aviso de teste na barra do sistema).
    await askPermission();
    setPerm(notificationPermission());
    setPrefs(loadNotifyPrefs());
  };

  const permLabel =
    perm === 'granted'
      ? t('chats.notifyAllowed')
      : perm === 'denied'
        ? t('chats.notifyDenied')
        : perm === 'unsupported'
          ? t('chats.notifyUnsupported')
          : t('chats.notifyNeedPerm');

  return (
    <div className="chat-notify-panel">
      <div className="border-b border-border px-3.5 py-3">
        <p className="text-[13px] font-semibold text-foreground">{t('chats.notifyTitle')}</p>
        <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{t('chats.notifyHint')}</p>
      </div>

      <div className="space-y-1 px-3.5 py-3">
        <div className="mb-2 rounded-lg border border-border bg-background px-2.5 py-2">
          <p className="text-[11px] leading-snug text-muted-foreground">{permLabel}</p>
          {perm === 'granted' ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-2 h-8 w-full text-[12px]"
              onClick={async () => {
                setAsking(true);
                setTestStatus('');
                try {
                  unlockChatAudio();
                  playChatSound('alert');
                  const result = await showChatDesktopNotification({
                    kind: 'chat.message',
                    title: 'Cliente exemplo',
                    contactName: 'Cliente exemplo',
                    contactPhone: '5511999887766',
                    body: t('chats.notifyTestRich'),
                    tag: `aurax-notify-test-${Date.now()}`,
                    force: true,
                  });
                  update({ desktopPush: true });
                  if (result?.ok) {
                    setTestStatus('ok');
                    toast.success(t('chats.notifyTestOk'));
                  } else if (result?.error === 'permission') {
                    setTestStatus('fail');
                    toast.error(t('chats.notifyDenied'));
                  } else {
                    setTestStatus('fail');
                    toast.error(t('chats.notifyTestFail'));
                  }
                } catch {
                  setTestStatus('fail');
                  toast.error(t('chats.notifyTestFail'));
                } finally {
                  setAsking(false);
                }
              }}
              disabled={asking}
            >
              <BellRing className="h-3.5 w-3.5" />
              {t('chats.notifyTestNow')}
            </Button>
          ) : null}
          {testStatus === 'ok' ? (
            <p className="mt-1.5 text-[10px] leading-snug text-emerald-500">{t('chats.notifyTestOk')}</p>
          ) : null}
          {testStatus === 'fail' ? (
            <p className="mt-1.5 text-[10px] leading-snug text-destructive">{t('chats.notifyTestFail')}</p>
          ) : null}
          {perm === 'granted' ? (
            <p className="mt-1.5 text-[10px] leading-snug text-muted-foreground">
              {t('chats.notifyFocusHint')}
            </p>
          ) : null}
          {!isStandalonePwa() ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="mt-2 h-8 w-full text-[12px]"
              onClick={() => {
                onClose?.();
                openPwaInstallPrompt();
              }}
              data-testid="pwa-install-from-notify"
            >
              <Download className="h-3.5 w-3.5" />
              {t('pwa.installBtn')}
            </Button>
          ) : null}
          {perm !== 'granted' && perm !== 'unsupported' && perm !== 'denied' ? (
            <Button
              type="button"
              size="sm"
              className="mt-2 h-8 w-full text-[12px]"
              onClick={askPermission}
              disabled={asking}
            >
              <BellRing className="h-3.5 w-3.5" />
              {asking ? t('chats.notifyAsking') : t('chats.notifyAllow')}
            </Button>
          ) : null}
          {perm === 'denied' ? (
            <p className="mt-2 text-[10px] leading-snug text-destructive">
              {t('chats.notifyDeniedHelp')}
            </p>
          ) : null}
        </div>

        <PrefRow
          label={t('chats.notifyPush')}
          hint={t('chats.notifyPushHint')}
          checked={!!prefs.desktopPush && perm === 'granted'}
          onCheckedChange={onPushToggle}
          disabled={perm === 'denied' || perm === 'unsupported' || asking}
        />

        {prefs.desktopPush && perm === 'granted' ? (
          <>
            <PrefRow
              label={t('chats.notifyCatChat')}
              hint={t('chats.notifyCatChatHint')}
              checked={prefs.notifyChat !== false}
              onCheckedChange={(on) => update({ notifyChat: !!on })}
            />
            <PrefRow
              label={t('chats.notifyCatTeam')}
              hint={t('chats.notifyCatTeamHint')}
              checked={prefs.notifyTeam !== false}
              onCheckedChange={(on) => update({ notifyTeam: !!on })}
            />
            <PrefRow
              label={t('chats.notifyCatCommerce')}
              hint={t('chats.notifyCatCommerceHint')}
              checked={prefs.notifyCommerce !== false}
              onCheckedChange={(on) => update({ notifyCommerce: !!on })}
            />
          </>
        ) : null}

        <div className="my-1.5 h-px bg-border" />

        <PrefRow
          label={t('chats.notifySounds')}
          hint={t('chats.notifySoundsHint')}
          checked={!!prefs.soundsEnabled}
          onCheckedChange={(on) => update({ soundsEnabled: on })}
        />
        <PrefRow
          label={t('chats.notifyReceive')}
          checked={!!prefs.soundReceive}
          disabled={!prefs.soundsEnabled}
          onCheckedChange={(on) => update({ soundReceive: on })}
          onPreview={() => playChatSound('receive', { preview: true })}
        />
        <PrefRow
          label={t('chats.notifySend')}
          checked={!!prefs.soundSend}
          disabled={!prefs.soundsEnabled}
          onCheckedChange={(on) => update({ soundSend: on })}
          onPreview={() => playChatSound('send', { preview: true })}
        />
        <PrefRow
          label={t('chats.notifyAlert')}
          checked={!!prefs.soundAlert}
          disabled={!prefs.soundsEnabled}
          onCheckedChange={(on) => update({ soundAlert: on })}
          onPreview={() => playChatSound('alert', { preview: true })}
        />
      </div>

      {showInboxLink ? (
        <div className="border-t border-border px-3.5 py-2.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 w-full text-[12px]"
            onClick={() => {
              onClose?.();
              navigate('/chats');
            }}
          >
            {t('chats.notifyOpenInbox')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function ChatNotifyTrigger({ className, labeled = false }) {
  const { t } = useLanguage();
  const [prefs, setPrefs] = useState(loadNotifyPrefs);
  const [open, setOpen] = useState(false);

  useEffect(() => subscribeNotifyPrefs(setPrefs), []);

  const muted = !prefs.soundsEnabled;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn(
            'h-9 gap-1.5 rounded-lg border-border bg-background text-[11px] font-medium text-muted-foreground hover:text-foreground',
            muted && 'text-muted-foreground',
            className,
          )}
          title={t('chats.notifyTitle')}
          aria-label={labeled ? t('chats.notifyTitle') : undefined}
          data-testid="chat-notify-prefs-btn"
        >
          {muted ? <VolumeX size={13} className="shrink-0" /> : <Volume2 size={13} className="shrink-0" />}
          <span className={labeled ? undefined : 'hidden sm:inline'}>{t(labeled ? 'chats.notifyTitle' : 'chats.notifyShort')}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[min(22rem,calc(100vw-1.5rem))] border-border bg-popover p-0 shadow-lg backdrop-blur-none"
      >
        <ChatNotifyPanel />
      </PopoverContent>
    </Popover>
  );
}

export function ChatNotifyBanner({ onDone }) {
  const { t } = useLanguage();
  const [asking, setAsking] = useState(false);

  const dismiss = () => {
    try {
      window.localStorage.setItem(CHAT_NOTIFY_BANNER_KEY, '1');
    } catch {
      /* ignore */
    }
    onDone?.();
  };

  const activate = async () => {
    setAsking(true);
    try {
      await requestNotifyPermission({
        title: t('chats.notifyTitle'),
        body: t('chats.notifyTestPush'),
      });
    } finally {
      setAsking(false);
      dismiss();
    }
  };

  return (
    <div className="chats-notify-banner" data-testid="chat-notify-banner">
      <div className="chats-notify-banner-top">
        <div className="chats-notify-banner-icon" aria-hidden>
          <Bell className="h-4 w-4" strokeWidth={1.75} />
        </div>
        <div className="chats-notify-banner-copy">
          <p className="chats-notify-banner-title">{t('chats.notifyBannerTitle')}</p>
          <p className="chats-notify-banner-hint">{t('chats.notifyBannerHint')}</p>
        </div>
      </div>
      <div className="chats-notify-banner-actions">
        <Button
          type="button"
          size="sm"
          className="chats-notify-banner-allow h-9 w-full rounded-[0.2rem] text-[12px] font-semibold"
          onClick={activate}
          disabled={asking}
        >
          {asking ? t('chats.notifyAsking') : t('chats.notifyAllow')}
        </Button>
        <button
          type="button"
          onClick={dismiss}
          className="chats-notify-banner-later"
        >
          {t('chats.notifyLater')}
        </button>
      </div>
    </div>
  );
}

export function HeaderNotifyBell({ unreadCount = 0, className = '', iconClassName = 'h-5 w-5' }) {
  const { t } = useLanguage();
  const queue = useHumanQueue();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(loadNotifyPrefs);
  const perm = notificationPermission();

  useEffect(() => {
    bootstrapChatNotify();
    return subscribeNotifyPrefs(setPrefs);
  }, []);

  const pushOn = prefs.desktopPush && perm === 'granted';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={t('header.notifications')}
          aria-label={t('header.notifications')}
          className={cn(
            'relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors',
            open ? 'bg-muted text-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            className,
          )}
          data-testid="header-notify-bell"
        >
          {prefs.soundsEnabled || pushOn ? <Bell className={iconClassName} /> : <BellOff className={iconClassName} />}
          {queue.total>0&&<span title={`${queue.total} aguardando humano`} className="absolute -bottom-1 -left-1 rounded-[3px] bg-amber-500 px-1 text-[9px] font-bold text-black">{queue.total>99?'99+':queue.total}</span>}
          {Math.max(unreadCount, queue.total) > 0 ? (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
              {Math.max(unreadCount, queue.total) > 99 ? '99+' : Math.max(unreadCount, queue.total)}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[min(22rem,calc(100vw-1.5rem))] border-border bg-popover p-0 shadow-lg backdrop-blur-none"
      >
        {queue.total > 0 && <section className="border-b border-border p-3"><strong className="text-sm">Aguardando humano · {queue.total}</strong><p className="text-xs text-muted-foreground">Encaminhados pelo bot. Atualização a cada 30 segundos.</p><div className="max-h-48 overflow-y-auto">{queue.items.map(row=><button type="button" key={row.id} className="flex w-full flex-col border-b border-border py-2 text-left text-xs" onClick={()=>{setOpen(false);navigate(`/chats?open=${encodeURIComponent(row.id)}`);}}><span className="font-semibold">{row.contact_name||'Contato'}</span><span className="text-muted-foreground">{row.connection_name} · {row.chatbot_handoff_at?`há ${Math.max(0,Math.floor((Date.now()-Date.parse(row.chatbot_handoff_at))/60000))} min`:'Aguardando'}</span></button>)}</div></section>}
        <ChatNotifyPanel showInboxLink onClose={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}
